import { auditEventInfo, DEFAULT_COMPANY_TIME_ZONE, formatTry, zonedDateKey, type AuditEntryDto, type AuditIcon } from '@dbrain/shared';
import { formatBytes, formatSeconds, formatTokens } from './format.js';
import { timeSuffix } from './turkish.js';
import { licenseRange } from './license-days.js';

/**
 * The audit log written for people (YonetimDenetim.dc.html, D-111): a
 * title, who did it, and the details in words ("Şirket hesabıyla · beni
 * hatırla: hayır", "Yanlış şifre · 2. deneme", "Üye başına varsayılan:
 * 2 Mn → 20 Mn"). The address is already masked when it is written; it is
 * shown apart, in grey. No raw key: value pairs; the CSV keeps the raw data.
 */
export interface AuditView {
  title: string;
  icon: AuditIcon;
  /** the actor's name, the typed user name of a failed sign-in, or null for the system */
  who: string | null;
  detail: string;
  ip: string | null;
  failure: boolean;
  /** Phase 16c.3: a failed skill install's error number (the one the person saw), searchable */
  errorId: string | null;
}

type T = (key: string, options?: Record<string, unknown>) => string;

/** the events shown as failures (red), whatever their result field says */
const FAILURES = new Set([
  'auth.login_failed',
  'skill.load_failed',
  'skill.install_failed',
  'skill.update_failed',
  'license.rejected',
  'system.backup_failed',
  'system.model_server_down',
  'microsoft.sign_in_failed',
  'microsoft.refresh_failed',
]);

/**
 * Microsoft 365 part 3: a write card that ended without a mail or a meeting is red when it failed, its end is unknown,
 * it expired or its run was stopped. The person's own "Vazgeç" (reason `cancelled`) is a normal row (v0.15g); the
 * server never counts these rows under "Yalnız uyarılar" (their result is `ok`).
 */
const NOT_DONE = new Set(['microsoft.mail_not_sent', 'microsoft.meeting_not_created']);

/** tool calls through a connection: an MCP server's, or Microsoft 365's (the built-in, Phase 15e) */
const TOOL_CALLS = new Set(['mcp.tool_called', 'microsoft.tool_called']);

/**
 * Microsoft 365's on/off settings as `settings_changed` names them ("Mail okuma: açıldı"); part 3 adds sending,
 * meetings and the two document switches ("Mail gönderme: açıldı", MsDenetim)
 */
const SWITCH_FIELDS = new Set(['enabled', 'mailRead', 'calendarRead', 'matchAccount', 'mailSend', 'calendarWrite', 'onedriveImport', 'sharepointImport']);

const text = (value: unknown): string | null => (typeof value === 'string' && value.trim() ? value : null);
const num = (value: unknown): number | null => (typeof value === 'number' && Number.isFinite(value) ? value : null);
const list = (value: unknown): string[] => (Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : []);
/** mail_read → mailRead: a field name as the API's body writes it, whichever way the metadata spells it */
const camel = (value: string): string => value.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase());

export function auditView(entry: AuditEntryDto, t: T, locale: string, domainName: (code: string) => string = (code) => code): AuditView {
  const info = auditEventInfo(entry.action);
  const lang = locale.startsWith('en') ? 'en' : 'tr';
  const m = entry.metadata;
  const tokens = (value: unknown, none: string) => {
    const n = num(value);
    return n === null ? none : formatTokens(n, locale);
  };
  const target = entry.target?.name ?? entry.target?.email ?? null;
  // Phase 16c: the bireysel platform log's words (the web's platformAudit.*); '' where a reader has none
  const platform = (key: string, options: Record<string, unknown> = {}) => t(`platformAudit.${key}`, { defaultValue: '', ...options });
  const day = (iso: string) => {
    const date = /^\d{4}-\d{2}-\d{2}$/.test(iso) ? new Date(`${iso}T12:00:00Z`) : new Date(iso);
    return Number.isNaN(date.getTime()) ? iso : date.toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric', timeZone: DEFAULT_COMPANY_TIME_ZONE });
  };
  // Microsoft 365's words are the web's `microsoft365.admin.*` (apps/web/src/i18n/m365-admin.*); a reader without
  // them (the iPhone app in this round) gets '' and shows the event's title alone, never a key
  const words = (key: string, options: Record<string, unknown> = {}) => t(`microsoft365.admin.${key}`, { defaultValue: '', ...options });
  const parts: string[] = [];
  let title = info.title[lang];
  switch (entry.action) {
    case 'auth.login': {
      if (m.created === true) title = t('admin.audit.titles.firstLogin');
      const method = text(m.method);
      if (method) parts.push(t(`admin.audit.method.${method}`, { defaultValue: method }));
      if (typeof m.remember === 'boolean') parts.push(t('admin.audit.remember', { value: t(m.remember ? 'common.yes' : 'common.no') }));
      if (m.linked === true) parts.push(t('admin.audit.linked'));
      break;
    }
    case 'auth.login_failed': {
      const reason = text(m.reason);
      parts.push(t(`admin.audit.reason.${reason ?? 'invalid'}`, { defaultValue: reason ?? '' }));
      const attempt = num(m.attempt);
      if (attempt !== null) parts.push(t('admin.audit.attempt', { count: attempt }));
      break;
    }
    case 'auth.refresh_refused':
      parts.push(t('admin.audit.refreshRefused'));
      break;
    case 'auth.domains_changed': {
      const added = list(m.added).map(domainName);
      const removed = list(m.removed).map(domainName);
      if (added.length > 0) parts.push(t('admin.audit.domainsAdded', { domains: added.join(', ') }));
      if (removed.length > 0) parts.push(t('admin.audit.domainsRemoved', { domains: removed.join(', ') }));
      const via = text(m.via);
      if (via) parts.push(t(`admin.audit.via.${via}`, { defaultValue: '' }));
      break;
    }
    case 'auth.role_changed':
    case 'tenant.member_role_changed': {
      if (target && entry.action === 'tenant.member_role_changed') parts.push(target);
      const from = text(m.from);
      const to = text(m.to);
      if (from || to) parts.push(`${from ? t(`roles.tenant.${from}`) : '—'} → ${to ? t(`roles.tenant.${to}`) : '—'}`);
      if (text(m.via)) parts.push(t(`admin.audit.via.${String(m.via)}`, { defaultValue: '' }));
      break;
    }
    case 'quota.changed': {
      const scope = text(m.scope);
      if (scope === 'organization_monthly') {
        title = t('admin.audit.titles.monthlyBudget');
        parts.push(t('admin.audit.budget.monthly', { from: tokens(m.from, t('admin.audit.budget.noLimit')), to: tokens(m.to, t('admin.audit.budget.noLimit')) }));
      } else if (scope === 'organization_daily') {
        title = t('admin.audit.titles.dailyBudget');
        parts.push(t('admin.audit.budget.memberDefault', { from: tokens(m.from, t('admin.audit.budget.platform')), to: tokens(m.to, t('admin.audit.budget.platform')) }));
      } else if (scope === 'domain') {
        title = t('admin.audit.titles.dailyBudget');
        parts.push(t('admin.audit.budget.domain', { domain: text(m.domainName) ?? domainName(text(m.domain) ?? ''), from: tokens(m.from, t('admin.audit.budget.organization')), to: tokens(m.to, t('admin.audit.budget.organization')) }));
      } else if (scope === 'member') {
        title = t('admin.audit.titles.dailyBudget');
        parts.push(t('admin.audit.budget.member', { name: target ?? t('admin.audit.someone'), from: tokens(m.from, t('admin.audit.budget.default')), to: tokens(m.to, t('admin.audit.budget.default')) }));
        const bulk = num(m.bulk);
        if (bulk !== null) parts.push(t('admin.audit.budget.bulk', { count: bulk }));
      } else {
        // entries from before Phase 13d-2
        const members = num(m.members);
        if (members) parts.push(t('admin.audit.budget.members', { count: members }));
      }
      break;
    }
    case 'quota.exceeded':
      parts.push(t(m.scope === 'tenant_monthly' ? 'admin.audit.exceeded.monthly' : 'admin.audit.exceeded.daily', { used: tokens(m.used, '—'), limit: tokens(m.limit, '—') }));
      break;
    case 'workspace.created':
    case 'workspace.deleted': {
      const name = text(m.name);
      if (name) parts.push(`"${name}"`);
      const kind = text(m.kind);
      if (kind) parts.push(t(`admin.usage.workspaces.kinds.${kind}`, { defaultValue: kind }));
      break;
    }
    case 'tenant.member_deactivated':
    case 'tenant.member_reactivated':
    case 'tenant.member_removed':
    case 'auth.sessions_revoked':
    case 'auth.break_glass_set': {
      if (target) parts.push(target);
      const ended = num(m.sessionsEnded) ?? num(m.sessions);
      if (ended !== null && ended > 0) parts.push(t('admin.audit.sessions', { count: ended }));
      break;
    }
    case 'auth.relink_required':
      if (target) parts.push(target);
      if (text(m.username)) parts.push(t('admin.audit.newUsername', { username: String(m.username) }));
      break;
    case 'auth.account_relinked':
      if (target) parts.push(target);
      parts.push(`${text(m.previousUsername) ?? '—'} → ${text(m.username) ?? '—'}`);
      break;
    case 'auth.provider_created':
    case 'auth.provider_changed': {
      const audience = text(m.audience);
      const base = text(m.baseUrl);
      if (audience) parts.push(audience);
      if (base) {
        try {
          parts.push(new URL(base).host);
        } catch {
          parts.push(base);
        }
      }
      break;
    }
    case 'identity.domain_opened':
      parts.push(text(m.displayName) ? `"${String(m.displayName)}"` : (text(m.domain) ?? ''));
      if (text(m.displayName) && text(m.domain)) parts.push(String(m.domain));
      break;
    case 'identity.domain_renamed':
      if (text(m.domain)) parts.push(String(m.domain));
      if (text(m.displayName)) parts.push(`→ "${String(m.displayName)}"`);
      break;
    case 'audit.exported':
      if (num(m.days)) parts.push(t('admin.audit.lastDays', { count: num(m.days) }));
      if (num(m.rows) !== null) parts.push(t('admin.audit.rows', { count: num(m.rows) }));
      break;
    case 'admin.exported':
      if (text(m.list)) parts.push(t(`admin.audit.lists.${String(m.list)}`, { defaultValue: String(m.list) }));
      if (num(m.rows) !== null) parts.push(t('admin.audit.rows', { count: num(m.rows) }));
      break;
    // Phase 16c.3: "diagram-design · github.com/cathrynlavery/diagram-design · Kişisel"
    case 'skill.installed': {
      const name = text(m.name);
      if (name) parts.push(name);
      const from = text(m.source);
      if (from) parts.push(from);
      const level = text(m.level);
      if (level) parts.push(t(`skills.levels.${level}`, { defaultValue: level }));
      break;
    }
    // "github.com/ornek-firma/sunum-sablonu · Skill çok büyük (26,3 MB)", "… · GitHub istek sınırı · 14:32’ye kadar"
    case 'skill.install_failed':
    case 'skill.update_failed': {
      const name = text(m.name);
      if (name && entry.action === 'skill.update_failed') parts.push(name);
      const from = text(m.source);
      if (from) parts.push(from);
      parts.push(skillFailureReason(m, t, locale));
      break;
    }
    case 'skill.created':
    case 'skill.load_failed': {
      const skill = text(m.skill) ?? text(m.name);
      if (skill) parts.push(skill);
      const level = text(m.level);
      if (level) parts.push(t(`admin.skills.scopes.${level === 'team' ? 'team' : level === 'user' || level === 'workspace' ? 'personal' : level}`, { defaultValue: level }));
      const reason = text(m.reason);
      if (reason) parts.push(t(`admin.audit.skillFailure.${reason}`, { defaultValue: reason }));
      break;
    }
    case 'license.uploaded':
    case 'license.applied':
    case 'license.rejected': {
      const id = text(m.licenseId);
      if (id) parts.push(id);
      const from = text(m.validFrom);
      const until = text(m.validUntil);
      if (from && until && /^\d{4}-\d{2}-\d{2}$/.test(from) && /^\d{4}-\d{2}-\d{2}$/.test(until)) parts.push(licenseRange(from, until, locale));
      const reason = text(m.reason);
      if (reason) parts.push(t(`license.upload.problems.${reason}`, { defaultValue: reason }));
      if (m.added === false) parts.push(t('license.upload.installed'));
      if (text(m.via) === 'license:dev') parts.push(t('license.page.history.tool'));
      break;
    }
    // the free trial (13f-2, D-132)
    case 'license.trial_started':
    case 'license.trial_ended': {
      const reason = text(m.reason);
      if (reason) parts.push(t(`admin.audit.trialEnded.${reason}`, { defaultValue: reason }));
      if (text(m.licenseId)) parts.push(String(m.licenseId));
      const from = text(m.validFrom);
      const until = text(m.validUntil);
      if (from && until && /^\d{4}-\d{2}-\d{2}$/.test(from) && /^\d{4}-\d{2}-\d{2}$/.test(until)) parts.push(licenseRange(from, until, locale));
      break;
    }
    case 'license.trial_reminder':
      if (num(m.daysLeft) !== null) parts.push(t('admin.audit.trialLeft', { count: num(m.daysLeft) }));
      break;
    case 'license.state_changed': {
      const state = (value: unknown) => (text(value) ? t(`license.page.status.${String(value)}`, { defaultValue: String(value) }) : '—');
      parts.push(`${state(m.from)} → ${state(m.to)}`);
      if (text(m.licenseId)) parts.push(String(m.licenseId));
      break;
    }
    // the models (Phase 13g, D-140): the company's and the platform operator's, in the same words
    case 'models.provider_added':
    case 'platform.provider_created': {
      if (text(m.name)) parts.push(`"${String(m.name)}"`);
      if (text(m.address)) parts.push(String(m.address));
      if ('models' in m) parts.push(num(m.models) === null ? t('admin.audit.models.unreachable') : t('admin.audit.models.found', { count: num(m.models) }));
      if (m.withKey === true) parts.push(t('admin.audit.models.withKey'));
      break;
    }
    case 'models.provider_updated':
    case 'platform.provider_updated': {
      if (text(m.name)) parts.push(`"${String(m.name)}"`);
      if (text(m.renamedFrom)) parts.push(t('admin.audit.models.renamed', { from: String(m.renamedFrom) }));
      if (text(m.address) && text(m.addressFrom)) parts.push(t('admin.audit.models.addressFrom', { from: String(m.addressFrom), address: String(m.address), defaultValue: '' }) || t('admin.audit.models.address', { address: String(m.address) }));
      else if (text(m.address)) parts.push(t('admin.audit.models.address', { address: String(m.address) }));
      else if (m.addressChanged === true) parts.push(t('admin.audit.models.addressChanged'));
      if (m.keyReplaced === true) parts.push(t('admin.audit.models.keyReplaced'));
      if (m.keyCleared === true) parts.push(t('admin.audit.models.keyCleared'));
      if (typeof m.enabled === 'boolean') parts.push(t(m.enabled ? 'admin.audit.models.turnedOn' : 'admin.audit.models.turnedOff'));
      break;
    }
    case 'models.provider_removed':
    case 'platform.provider_deleted':
      if (text(m.name)) parts.push(`"${String(m.name)}"`);
      break;
    case 'models.provider_tested':
    case 'platform.provider_tested':
      if (text(m.name)) parts.push(`"${String(m.name)}"`);
      if (typeof m.ok === 'boolean') parts.push(t(m.ok ? 'admin.audit.models.testOk' : 'admin.audit.models.testFailed'));
      if (text(m.model)) parts.push(t('admin.audit.models.testModel', { model: String(m.model) }));
      break;
    case 'models.models_refreshed':
    case 'platform.models_refreshed':
      if (text(m.name)) parts.push(`"${String(m.name)}"`);
      if (num(m.added) !== null) parts.push(t('admin.audit.models.refreshed', { added: num(m.added), missing: num(m.missing) ?? 0 }));
      break;
    case 'models.model_updated':
    case 'platform.model_updated': {
      if (text(m.model)) parts.push(String(m.model));
      const fields = text(m.fields)?.split(',').filter(Boolean) ?? [];
      // Phase 16c: old → new where the entry has them (earlier entries name the fields only); 16c.1 the kind, the
      // picker's line, "Sohbette" and who may use it, in words
      const value = (f: string, v: unknown) =>
        v === null || v === undefined
          ? f === 'description' || f === 'teams'
            ? '—'
            : t('admin.audit.models.unset', { defaultValue: '—' })
          : f === 'kind' && typeof v === 'string'
            ? t(`models.kinds.${v}`, { defaultValue: v })
            : f === 'chatAudience' && typeof v === 'string'
              ? t(`admin.audit.models.audiences.${v}`, { defaultValue: v })
              : typeof v === 'boolean'
                ? t(v ? 'admin.audit.models.on' : 'admin.audit.models.off', { defaultValue: String(v) })
                : typeof v === 'number'
                  ? v.toLocaleString(locale)
                  : f === 'description'
                    ? `"${String(v)}"`
                    : String(v);
      const said = fields.map((f) => {
        const label = t(`admin.audit.models.fields.${f}`, { defaultValue: f });
        return `${f}From` in m || `${f}To` in m ? `${label} ${value(f, m[`${f}From`])} → ${value(f, m[`${f}To`])}` : label;
      });
      if (said.length > 0) parts.push(said.join(', '));
      break;
    }
    case 'models.task_model_changed':
    case 'platform.route_changed': {
      const task = text(m.task) ?? text(m.callType);
      if (!task) break;
      const taskName = t(`models.routes.tasks.${task}`, { defaultValue: task });
      // entries from before Phase 13g name only the task
      if (!('from' in m) && !('to' in m)) {
        parts.push(taskName);
        break;
      }
      const vector = ['embedding', 'rerank', 'ocr'].includes(task);
      const empty = t(vector ? 'admin.audit.models.none' : 'admin.audit.models.mainModel');
      parts.push(`${taskName}: ${text(m.from) ?? empty} → ${text(m.to) ?? empty}`);
      break;
    }
    case 'platform.internal_providers_changed':
      title = t(m.allowed === true ? 'admin.audit.titles.internalOn' : 'admin.audit.titles.internalOff');
      if (text(m.company)) parts.push(String(m.company));
      parts.push(t(m.allowed === true ? 'admin.audit.models.internalOn' : 'admin.audit.models.internalOff'));
      break;
    // the system (Phase 13g, D-140, D-141; the backups from Phase 15a). Phase 16c: a backup says how it started (the
    // night's, by hand, the installation's first); rows from before name no start and keep the plain title
    case 'system.backup_done': {
      if (m.trigger === 'nightly') title = t('admin.audit.titles.nightlyBackup', { defaultValue: title });
      if (m.trigger === 'manual') parts.push(t(m.first === true ? 'admin.audit.system.first' : 'admin.audit.system.manual', { defaultValue: '' }));
      const what = m.files === false ? 'database' : m.database === false ? 'files' : 'both';
      if (!(m.trigger === 'manual' && m.first === true)) {
        const said = t(`admin.audit.system.backup.${what}`);
        parts.push(parts.some(Boolean) ? said.charAt(0).toLocaleLowerCase(locale) + said.slice(1) : said);
      }
      if (num(m.bytes) !== null) parts.push(formatBytes(num(m.bytes) as number, locale));
      if (num(m.kept) !== null) parts.push(t('admin.audit.system.kept', { count: num(m.kept) }));
      break;
    }
    case 'system.backup_failed':
      if (m.trigger === 'nightly') title = t('admin.audit.titles.nightlyBackupFailed', { defaultValue: title });
      if (m.trigger === 'manual') parts.push(t('admin.audit.system.manual', { defaultValue: '' }));
      if (text(m.reason)) parts.push(t(`admin.audit.system.backupReason.${String(m.reason)}`, { defaultValue: String(m.reason) }));
      break;
    case 'system.updated':
      // Phase 16c: the first start of an installation is its installation, not an update
      if (!text(m.from)) title = t('admin.audit.titles.installed', { defaultValue: title });
      parts.push(text(m.from) ? `${String(m.from)} → ${text(m.to) ?? '—'}` : (text(m.to) ?? '—'));
      if (!text(m.from)) parts.push(t('admin.audit.system.firstInstall', { defaultValue: '' }));
      if (m.migrated === true) parts.push(t('admin.audit.system.migrated'));
      if (m.healthy === true) parts.push(t('admin.audit.system.healthy'));
      break;
    // Phase 16c: scripts/server/mail-test.sh's test mail
    case 'system.mail_test':
    case 'system.mail_test_failed':
      if (text(m.host)) parts.push(String(m.host));
      if (text(m.to)) parts.push(t('admin.audit.system.mailTo', { to: m.to, defaultValue: String(m.to) }));
      if (text(m.kind)) parts.push(t('admin.audit.system.mailKind', { kind: m.kind, defaultValue: String(m.kind) }));
      break;
    // Phase 16c (B2cPlatformDenetim): the bireysel platform log's own events, each in plain words; the texts are the
    // web's `platformAudit.*` (a reader without them gets the title alone)
    case 'platform.model_share_changed': {
      const share = num(m.from) !== num(m.to);
      if (!share && text(m.orderFrom) !== text(m.orderTo)) title = platform('queueChanged') || title;
      if (share) parts.push(platform('share', { from: num(m.from), to: num(m.to) }));
      if (text(m.orderTo)) parts.push(platform('order', { order: platform(`orders.${String(m.orderTo)}`) }));
      break;
    }
    case 'platform.sandbox_cap_changed':
      parts.push(platform('sandboxes', { from: num(m.from), to: num(m.to) }));
      break;
    case 'platform.pro_granted':
    case 'platform.pro_revoked': {
      parts.push(platform('to', { person: entry.target?.name ?? text(m.person) ?? '—' }));
      if (text(m.reason)) parts.push(platform('reason', { reason: m.reason }));
      if (text(m.until)) parts.push(platform('until', { date: day(String(m.until)) }));
      break;
    }
    case 'system.pro_ended':
      if (text(m.reason)) parts.push(platform('reason', { reason: m.reason }));
      if (text(m.endedAt)) parts.push(platform('endedOn', { date: day(String(m.endedAt)) }));
      break;
    case 'platform.plan_updated':
    case 'platform.plan_change_scheduled':
    case 'platform.plan_change_cancelled':
    case 'system.plan_change_applied': {
      if (text(m.plan)) parts.push(String(m.plan));
      const lira = (value: unknown) => (num(value) === null ? '—' : formatTry(num(value) as number, locale));
      if ('monthlyFrom' in m || 'monthlyTo' in m) parts.push(platform('monthly', { from: lira(m.monthlyFrom), to: lira(m.monthlyTo) }));
      if (('yearlyFrom' in m || 'yearlyTo' in m) && m.yearlyFrom !== m.yearlyTo) parts.push(platform('yearly', { from: lira(m.yearlyFrom), to: lira(m.yearlyTo) }));
      // the operator's change names them dailyTokensFrom…, the plan job's applied change fromDailyTokens…
      for (const [key, unit] of [['dailyTokens', 'tokens'], ['maxDocuments', 'documents'], ['maxFileMb', 'mb']] as const) {
        const cap = key.charAt(0).toUpperCase() + key.slice(1);
        const from = m[`${key}From`] ?? m[`from${cap}`];
        const to = m[`${key}To`] ?? m[`to${cap}`];
        if (num(from) !== null && num(to) !== null && from !== to) parts.push(platform(unit, { from: unit === 'tokens' ? formatTokens(num(from) as number, locale) : num(from), to: unit === 'tokens' ? formatTokens(num(to) as number, locale) : num(to) }));
      }
      // 16c.1: the own skill and connection limits as counts (null: no limit); before, connections were on or off
      const limit = (value: unknown) => (value === null ? platform('unlimited') : String(num(value) ?? '—'));
      for (const [key, words] of [['maxOwnSkills', 'ownSkills'], ['maxOwnConnections', 'ownConnections']] as const) {
        const cap = key.charAt(0).toUpperCase() + key.slice(1);
        const from = `${key}From` in m ? m[`${key}From`] : m[`from${cap}`];
        const to = `${key}To` in m ? m[`${key}To`] : m[`to${cap}`];
        if ((`${key}From` in m || `from${cap}` in m) && from !== to) parts.push(platform(words, { from: limit(from), to: limit(to) }));
      }
      const connFrom = m.ownConnectionsFrom ?? m.fromOwnConnections;
      const connTo = m.ownConnectionsTo ?? m.toOwnConnections;
      if (!('maxOwnConnectionsFrom' in m) && !('fromMaxOwnConnections' in m) && typeof connFrom === 'boolean' && typeof connTo === 'boolean' && connFrom !== connTo) {
        parts.push(platform(connTo ? 'connectionsOn' : 'connectionsOff'));
      }
      // 16c.1: the package's chat models and the one a new chat starts with, by name
      if ('chatModelsFrom' in m || 'chatModelsTo' in m) parts.push(platform('chatModels', { from: text(m.chatModelsFrom) ?? '—', to: text(m.chatModelsTo) ?? '—' }));
      if ('defaultModelFrom' in m || 'defaultModelTo' in m) parts.push(platform('defaultModel', { from: text(m.defaultModelFrom) ?? '—', to: text(m.defaultModelTo) ?? '—' }));
      if (text(m.effectiveAt)) parts.push(platform('effective', { date: day(String(m.effectiveAt)) }));
      if (num(m.subscribers)) parts.push(platform('subscribers', { count: num(m.subscribers) }));
      break;
    }
    case 'platform.fair_use_mail_sent':
      parts.push(platform('to', { person: entry.target?.name ?? text(m.person) ?? '—' }));
      break;
    case 'account.opened':
      if (text(m.method)) parts.push(platform(`method.${String(m.method)}`));
      if (m.operator === true) parts.push(platform('operator'));
      break;
    // 16c.1: where a person changed their marketing consent (Ayarlar, or a mail's unsubscribe link)
    case 'account.marketing_withdrawn':
    case 'account.marketing_given':
      if (text(m.via)) parts.push(platform(`via.${String(m.via)}`));
      break;
    case 'system.pro_open_mailed':
      if (num(m.count) !== null) parts.push(platform('people', { count: num(m.count) }));
      break;
    case 'billing.subscription_started':
    case 'billing.renewed':
      if (text(m.period)) parts.push(platform(`periods.${String(m.period)}`));
      if (num(m.amount) !== null) parts.push(formatTry(num(m.amount) as number, locale));
      if (text(m.until)) parts.push(platform('until', { date: day(String(m.until)) }));
      if (num(m.attempts) !== null && (num(m.attempts) as number) > 1) parts.push(platform('onRetry'));
      break;
    case 'billing.checkout_failed':
      if (num(m.amount) !== null) parts.push(formatTry(num(m.amount) as number, locale));
      if (text(m.code)) parts.push(platform(`failures.${String(m.code)}`) || String(m.code));
      if (m.refunded === true) parts.push(platform('refundedAuto'));
      break;
    case 'billing.payment_failed':
      if (num(m.amount) !== null) parts.push(formatTry(num(m.amount) as number, locale));
      if (text(m.reason)) parts.push(String(m.reason));
      if (m.final === true) parts.push(platform('lastTry'));
      else if (text(m.nextRetryAt)) parts.push(platform('retryOn', { n: (num(m.retries) ?? 0) + 1, date: day(String(m.nextRetryAt)) }));
      break;
    case 'billing.subscription_ended':
      if (text(m.reason)) parts.push(platform(`ended.${String(m.reason)}`));
      break;
    case 'billing.cancel_requested':
      if (text(m.reason)) parts.push(platform(`cancelReasons.${String(m.reason)}`));
      if (text(m.until)) parts.push(platform('freeFrom', { date: day(String(m.until)) }));
      break;
    case 'billing.cancel_undone':
      if (text(m.until)) parts.push(platform('until', { date: day(String(m.until)) }));
      break;
    case 'billing.period_changed':
      if (text(m.from) && text(m.to)) parts.push(`${platform(`periods.${String(m.from)}`)} → ${platform(`periods.${String(m.to)}`)}`);
      if (text(m.on)) parts.push(platform('from', { date: day(String(m.on)) }));
      break;
    case 'billing.card_changed':
      if (text(m.last4)) parts.push(platform('card', { last4: m.last4 }));
      break;
    case 'billing.terms_applied':
      if (num(m.monthlyPrice) !== null) parts.push(platform('newMonthly', { price: formatTry(num(m.monthlyPrice) as number, locale) }));
      if (num(m.dailyTokens) !== null) parts.push(platform('newTokens', { tokens: formatTokens(num(m.dailyTokens) as number, locale) }));
      break;
    case 'platform.payment_refunded':
      parts.push(platform('to', { person: entry.target?.name ?? text(m.person) ?? '—' }));
      if (num(m.amount) !== null) parts.push(formatTry(num(m.amount) as number, locale));
      if (text(m.reason)) parts.push(platform('reason', { reason: m.reason }));
      break;
    case 'platform.invoice_issued':
      parts.push(platform('to', { person: entry.target?.name ?? text(m.person) ?? '—' }));
      if (text(m.number)) parts.push(String(m.number));
      if (num(m.amount) !== null) parts.push(formatTry(num(m.amount) as number, locale));
      break;
    case 'platform.billing_exported':
      if (text(m.month)) parts.push(String(m.month));
      if (num(m.rows) !== null) parts.push(platform('rows', { count: num(m.rows) }));
      break;
    case 'system.model_server_down':
      if (text(m.provider)) parts.push(String(m.provider));
      parts.push(t('admin.audit.system.noAnswer'));
      break;
    case 'system.model_server_up': {
      if (text(m.provider)) parts.push(String(m.provider));
      const ms = num(m.durationMs);
      if (ms !== null) parts.push(t('admin.audit.system.lasted', { duration: formatSeconds(Math.max(1, ms / 1000), locale) }));
      const retries = num(m.retries);
      if (retries) parts.push(t('admin.audit.system.retried', { count: retries }));
      const failed = num(m.failedCalls);
      if (failed) parts.push(t('admin.audit.system.failedCalls', { count: failed }));
      break;
    }
    case 'demo.seeded':
    case 'demo.cleared':
      if (num(m.people) !== null) parts.push(t('admin.audit.demoPeople', { count: num(m.people) }));
      if (num(m.domains) !== null) parts.push(t('admin.audit.demoDomains', { count: num(m.domains) }));
      break;
    // connections (D-181): the connection's name first; a tool call says how it was decided and how it ended
    case 'mcp.server_created':
    case 'mcp.server_deleted':
    case 'mcp.server_trusted':
    case 'mcp.server_untrusted':
    case 'mcp.oauth_client_set':
    case 'mcp.oauth_client_cleared':
    case 'mcp.server_updated': {
      if (text(m.name)) parts.push(String(m.name));
      if (text(m.scope)) parts.push(t(`admin.audit.connections.scopes.${String(m.scope)}`, { defaultValue: '' }));
      if (m.configChanged === true) parts.push(t('admin.audit.connections.configChanged'));
      for (const change of text(m.policiesChanged)?.split(',').filter(Boolean) ?? []) {
        const [tool, move] = change.split(':');
        const [from, to] = (move ?? '').split('>');
        parts.push(`${tool}: ${t(`admin.audit.connections.policy.${from}`, { defaultValue: from ?? '' })} → ${t(`admin.audit.connections.policy.${to}`, { defaultValue: to ?? '' })}`);
      }
      break;
    }
    case 'mcp.tool_policy_changed':
      if (text(m.name)) parts.push(String(m.name));
      parts.push(`${text(m.tool) ?? '—'}: ${t(`admin.audit.connections.policy.${String(m.from)}`, { defaultValue: String(m.from) })} → ${t(`admin.audit.connections.policy.${String(m.to)}`, { defaultValue: String(m.to) })}`);
      break;
    case 'mcp.account_connected':
    case 'mcp.account_disconnected':
      if (text(m.name)) parts.push(String(m.name));
      if (text(m.method)) parts.push(t(`admin.audit.connections.method.${String(m.method)}`, { defaultValue: '' }));
      if (m.shared === true) parts.push(t('admin.audit.connections.shared'));
      break;
    case 'mcp.tool_called':
    case 'microsoft.tool_called': {
      // the built-in's tools by their titles ("Mail ara"); a connection's by their own names
      const tool = text(m.tool);
      const shown = tool && entry.action === 'microsoft.tool_called' ? words(`audit.tools.${tool}`) || tool : tool;
      parts.push([text(m.server), shown].filter(Boolean).join(' · '));
      const decision = text(m.decision) ?? 'auto';
      const reason = text(m.reason);
      const option = text(m.option);
      if (decision === 'auto') parts.push(t('admin.audit.connections.auto', { reason: t(`admin.audit.connections.reasons.${reason ?? 'other'}`, { defaultValue: reason ?? '' }) }));
      else if (decision === 'approved') parts.push(t(option && option !== 'allow_once' ? 'admin.audit.connections.approvedAlways' : 'admin.audit.connections.approved'));
      else if (decision === 'denied') parts.push(t('admin.audit.connections.denied', { reason: t(`admin.audit.connections.reasons.${reason === 'rule' ? 'rule_deny' : (reason ?? 'other')}`, { defaultValue: reason ?? '' }) }));
      else parts.push(t('admin.audit.connections.rejected'));
      if (text(m.result)) parts.push(t(`admin.audit.connections.result.${String(m.result)}`));
      const ms = num(m.durationMs);
      if (ms !== null) parts.push(ms < 60_000 ? t('admin.audit.connections.seconds', { seconds: (ms / 1000).toLocaleString(locale, { maximumFractionDigits: 1 }) }) : formatSeconds(ms / 1000, locale));
      if (m.helper === true) parts.push(t('admin.audit.connections.helper'));
      if (entry.action === 'microsoft.tool_called' && num(m.items) !== null) parts.push(words('audit.items', { count: num(m.items) }));
      if (text(m.conversationId)) parts.push(t('admin.audit.connections.conversation', { id: String(m.conversationId).slice(0, 8) }));
      break;
    }
    // Microsoft 365, the built-in connection (Phase 15e, D-199): what changed and why, never mail, attachment or calendar content
    case 'microsoft.setup_saved': {
      // the setup body's field names, comma-separated (entraTenantId, clientId, clientSecret, signInMethod, secretExpiresOn)
      const fields = (Array.isArray(m.fields) ? list(m.fields) : (text(m.fields)?.split(',') ?? []))
        .map((field) => words(`audit.fields.${camel(field.trim())}`))
        .filter(Boolean);
      if (fields.length > 0) parts.push(words('audit.changed', { fields: fields.join(', ') }));
      if (text(m.method)) parts.push(words(`audit.method.${String(m.method)}`));
      const reset = num(m.reset);
      if (reset) parts.push(words('audit.reset', { count: reset }));
      else if (m.reset === true) parts.push(words('audit.resetAll'));
      break;
    }
    case 'microsoft.setup_removed': {
      const accounts = num(m.accounts);
      if (accounts !== null) parts.push(accounts > 0 ? words('audit.removed', { count: accounts }) : words('audit.removedNone'));
      break;
    }
    case 'microsoft.tested':
      if (m.ok === true) parts.push(words('audit.testOk'));
      else if (m.ok === false) {
        const code = text(m.code);
        const reason = words(`testCodes.${code ?? 'unexpected'}.title`) || words('testCodes.unexpected.title');
        parts.push(words('audit.testFailed', { reason }));
      }
      break;
    case 'microsoft.settings_changed': {
      // one row per change: {field, from, to}; the chosen teams as {field: 'teams', count}
      const field = camel(text(m.field) ?? '');
      const on = m.to === true || m.to === 'true';
      if (SWITCH_FIELDS.has(field)) parts.push(words(`audit.setting.${field}${on ? 'On' : 'Off'}`));
      else if (field === 'availability') {
        const side = (value: unknown) => (text(value) ? words(`audit.audience.${String(value)}`) || '—' : '—');
        parts.push(words('audit.setting.availability', { from: side(m.from), to: side(m.to) }));
      } else if (field === 'teams') parts.push(words('audit.setting.teams', { count: num(m.count) ?? 0 }));
      break;
    }
    case 'microsoft.team_changed': {
      const team = text(m.team) ?? text(m.teamName) ?? target;
      if (typeof m.enabled === 'boolean') parts.push(team ? words(m.enabled ? 'audit.teamOn' : 'audit.teamOff', { team }) : words(m.enabled ? 'audit.anyTeamOn' : 'audit.anyTeamOff'));
      break;
    }
    case 'microsoft.account_connected': {
      if (text(m.email)) parts.push(String(m.email));
      const scopes = (Array.isArray(m.scopes) ? list(m.scopes) : (text(m.scopes)?.split(',') ?? [])).map((scope) => words(`audit.scopes.${scope.trim()}`)).filter(Boolean);
      if (scopes.length > 0) parts.push([...new Set(scopes)].join(', '));
      // part 3: connected again for a new permission ("Yeni izin gerekiyor · yeniden bağlan")
      if (m.upgrade === true) parts.push(words('audit.upgrade'));
      if (m.method === 'device_code') parts.push(words('audit.deviceCode'));
      break;
    }
    case 'microsoft.account_disconnected':
      parts.push(words(`audit.disconnected.${text(m.reason) ?? 'other'}`) || words('audit.disconnected.other'));
      break;
    case 'microsoft.sign_in_failed':
      parts.push(words(`audit.signIn.${text(m.reason) ?? 'unknown'}`) || words('audit.signIn.unknown'));
      if (m.method === 'device_code') parts.push(words('audit.deviceCode'));
      break;
    case 'microsoft.refresh_failed':
      parts.push(words(`audit.refresh.${text(m.code) ?? 'unknown'}`) || words('audit.refresh.unknown'));
      break;
    case 'microsoft.consent_requested': {
      parts.push(words('audit.consentRequested'));
      const notified = num(m.notified) ?? num(m.admins);
      if (notified) parts.push(words('audit.notified', { count: notified }));
      break;
    }
    // Microsoft 365 part 3: one row per write card's end and per copy; counts only (MsDenetim)
    case 'microsoft.mail_sent':
    case 'microsoft.meeting_created': {
      parts.push(words('audit.write.product'));
      const people = num(entry.action === 'microsoft.mail_sent' ? m.recipients : m.attendees);
      if (entry.action === 'microsoft.mail_sent') {
        if (people !== null) parts.push(words('audit.write.recipients', { count: people }));
      } else if (people !== null) parts.push(people > 0 ? words('audit.write.attendees', { count: people }) : words('audit.write.noAttendees'));
      const external = num(m.external);
      if (external) parts.push(words('audit.write.external', { count: external }));
      if (entry.action === 'microsoft.meeting_created' && typeof m.online === 'boolean') parts.push(words(m.online ? 'audit.write.teams' : 'audit.write.noTeams'));
      if (m.edited === true) parts.push(words('audit.write.edited'));
      parts.push(words('audit.write.noContent'));
      break;
    }
    case 'microsoft.mail_not_sent':
    case 'microsoft.meeting_not_created': {
      const group = entry.action === 'microsoft.mail_not_sent' ? 'notSent' : 'notCreated';
      const reason = text(m.reason) ?? 'failed';
      const code = text(m.code);
      const why = code ? words(`audit.write.codes.${code}`) : '';
      const said = words(`audit.write.${group}.${reason}`) || words(`audit.write.${group}.failed`);
      parts.push(why ? `${said}: ${why}` : said);
      if (m.draftLeft === true) parts.push(words('audit.write.draftLeft'));
      break;
    }
    case 'microsoft.documents_imported':
    case 'microsoft.attachment_saved': {
      const source = entry.action === 'microsoft.attachment_saved' ? 'outlook' : (text(m.source) ?? '');
      // a batch from several sources says how many files each asked for ("OneDrive 2, SharePoint 1")
      const mixed = source === 'mixed' ? (['onedrive', 'sharepoint', 'outlook'] as const).filter((s) => (num(m[s]) ?? 0) > 0).map((s) => `${words(`audit.documents.source.${s}`)} ${num(m[s])}`) : [];
      const shown = mixed.length > 0 ? mixed.join(', ') : words(`audit.documents.source.${source}`);
      if (shown) parts.push(shown);
      const added = num(m.added) ?? (entry.action === 'microsoft.attachment_saved' && m.duplicate !== true ? 1 : null);
      const target = text(m.target);
      const name = text(m.targetName);
      const place = target
        ? words(`audit.documents.place.${target}${(target === 'team' || target === 'workspace') && !name ? 'Any' : ''}`, name ? { name } : {})
        : '';
      if (added !== null && place) {
        const to = words('audit.documents.to', { files: words('audit.documents.files', { count: added }), place });
        parts.push([to, m.acknowledged === true ? words('audit.documents.acknowledged') : ''].filter(Boolean).join(' '));
      } else if (added !== null) parts.push(words('audit.documents.files', { count: added }));
      const refused = num(m.refused);
      if (refused) parts.push(words('audit.documents.refused', { count: refused }));
      const duplicate = num(m.duplicate) ?? (m.duplicate === true ? 1 : null);
      if (duplicate) parts.push(words('audit.documents.duplicate', { count: duplicate }));
      break;
    }
    case 'microsoft.document_refetched': {
      const shown = words(`audit.documents.source.${text(m.source) ?? ''}`);
      if (shown) parts.push(shown);
      const result = text(m.result) ?? 'ok';
      const said = words(`audit.documents.refetch.${result}`) || words('audit.documents.refetch.ok');
      const code = text(m.code);
      // the worker's document codes (source_forbidden…), not a write's
      const why = result === 'failed' && code ? words(`audit.documents.codes.${code}`) : '';
      parts.push(why ? `${said}: ${why}` : said);
      break;
    }
    default:
      if (target) parts.push(target);
  }
  const failure =
    entry.result !== 'ok' ||
    FAILURES.has(entry.action) ||
    (NOT_DONE.has(entry.action) && m.reason !== 'cancelled') ||
    (TOOL_CALLS.has(entry.action) && m.result === 'error') ||
    (entry.action === 'microsoft.tested' && m.ok === false) ||
    (entry.action === 'microsoft.document_refetched' && m.result === 'failed');
  return {
    title,
    icon: info.icon,
    who: entry.actor ? (entry.actor.name ?? entry.actor.email) : text(m.username),
    detail: parts.filter(Boolean).join(' · '),
    ip: text(m.ip),
    failure,
    errorId: entry.action.startsWith('skill.') ? text(m.errorId) : null,
  };
}

/** Why a skill could not be installed or updated, in a few words (Phase 16c.3, SkillGitHubHatalar's Denetim rows). */
function skillFailureReason(m: Record<string, unknown>, t: T, locale: string): string {
  const reason = text(m.reason) ?? 'other';
  const say = (key: string, options: Record<string, unknown> = {}) => t(`admin.audit.skill.reason.${key}`, { defaultValue: '', ...options });
  switch (reason) {
    case 'skill_too_large': {
      const bytes = num(m.bytes);
      if (m.over === 'files' && num(m.files) !== null) return say('skill_too_large_files', { files: (num(m.files) as number).toLocaleString(locale) });
      return bytes !== null ? say('skill_too_large', { size: formatBytes(bytes, locale) }) : say('skill_too_large', { size: '—' });
    }
    case 'skill_source_rate_limited': {
      const reset = text(m.resetAt);
      if (!reset) return say('skill_source_rate_limited');
      const time = new Date(reset).toLocaleTimeString(locale.startsWith('en') ? 'en-GB' : locale, { hour: '2-digit', minute: '2-digit', timeZone: DEFAULT_COMPANY_TIME_ZONE });
      return say('rateUntil', { time, to: locale.startsWith('en') ? '' : timeSuffix(time, 'dative') });
    }
    case 'skill_source_not_allowed':
      return say('skill_source_not_allowed', { hosts: text(m.hosts) ?? '' });
    case 'skill_source_timeout':
      return say('skill_source_timeout', { seconds: Math.round((num(m.timeoutMs) ?? 60_000) / 1000) });
    case 'skill_source_unavailable':
      return num(m.status) !== null ? say('unavailableStatus', { status: m.status }) : say('skill_source_unavailable');
    default: {
      const known = say(reason);
      return known || say('other', { code: reason }) || reason;
    }
  }
}

/** The company's day of an entry ("2026-10-28", D-119): the rows are grouped by it. */
export function dayKey(iso: string, timeZone = DEFAULT_COMPANY_TIME_ZONE): string {
  return zonedDateKey(new Date(iso), timeZone);
}
